import { apiClient } from '@app/api';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { useYoutubeStatsEnabled } from '@app/modules/audit/hooks/useYoutubeStatsEnabled';
import { date, now, toTimestamp } from '@mi-front/core';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { YoutubeLiveCounterDisplay } from '../YoutubeLiveCounterDisplay';

/**
 * Live viewer chip backed by the legacy logger `yt-data` API (`fetchYoutubeStats`).
 */
export function YoutubeLiveCounterV1() {
  const station = useActiveStation();
  const youtubeStatsEnabled = useYoutubeStatsEnabled(station);

  const { data: youtubeStats, isLoading } = useQuery({
    queryKey: ['youtube-live-stats', station.id],
    queryFn: () =>
      apiClient.audit.fetchYoutubeStats(station.youtubeChannelId!, {
        from: toTimestamp(date().subtract(2, 'minutes').toDate()),
        to: toTimestamp(now()),
      }),
    enabled: youtubeStatsEnabled,
    refetchInterval: 60000,
  });

  const { viewers, isOffline, time } = useMemo(() => {
    const last = youtubeStats?.[youtubeStats.length - 1];
    const hasChannelBlock = Boolean(last?.channelId);
    const raw =
      hasChannelBlock && station.youtubeChannelId
        ? last!.channelId[0][station.youtubeChannelId]
        : undefined;
    const count = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
    const offline =
      !isLoading && (!hasChannelBlock || typeof raw !== 'number' || !Number.isFinite(raw));

    return {
      viewers: count,
      isOffline: offline,
      time: !offline && last?.time != null ? (last.time as string | number) : null,
    };
  }, [isLoading, station.youtubeChannelId, youtubeStats]);

  return youtubeStatsEnabled && !isLoading ? (
    <YoutubeLiveCounterDisplay viewers={viewers} isOffline={isOffline} time={time} />
  ) : null;
}
